import { useQueryClient } from '@tanstack/react-query';
import { BellOff, CheckCircle2, Copy, Loader2, RotateCcw } from 'lucide-react';
import { useState } from 'react';

import {
  getAlertasAdminControllerListarQueryKey,
  getAlertasAdminControllerObterQueryKey,
  getAlertasAdminControllerResumoQueryKey,
  useAlertasAdminControllerAlterarStatus,
  useAlertasAdminControllerListarOcorrencias,
  useAlertasAdminControllerObter,
} from '@/api/generated/admin-alertas/admin-alertas';
import type { OcorrenciaDeAlertaResponse, StatusAlerta } from '@/api/generated/model';
import { ApiError } from '@/api/http-client';
import {
  CLASSE_SEVERIDADE,
  dataHora,
  jsonLegivel,
  ROTULO_ORIGEM,
  ROTULO_SEVERIDADE,
  ROTULO_STATUS,
} from '@/features/admin/alertas/rotulos';
import { EstadoErro } from '@/shared/components/estados';
import { usePermissoes } from '@/shared/permissoes/usePermissoes';
import { Badge } from '@/shared/ui/badge';
import { Button } from '@/shared/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/ui/dialog';
import { Skeleton } from '@/shared/ui/skeleton';
import { toast } from '@/shared/ui/sonner';

interface Props {
  alertaId: string;
  aoFechar: () => void;
}

/**
 * Detalhe de um alerta: o resumo, o que fazer com ele e as últimas
 * ocorrências — cada uma com a requisição enviada e a resposta de erro.
 *
 * Busca o alerta pelo id (e não recebe a linha da tabela) porque é também o
 * destino do link do e-mail: `/admin/alertas/:id` abre a tela já com isto.
 */
export function DetalheDoAlertaDialogo({ alertaId, aoFechar }: Props) {
  const { pode } = usePermissoes();
  const queryClient = useQueryClient();
  const alerta = useAlertasAdminControllerObter(alertaId);
  const ocorrencias = useAlertasAdminControllerListarOcorrencias(alertaId, { pagina: 1, porPagina: 20 });
  const alterarStatus = useAlertasAdminControllerAlterarStatus();

  async function mudarPara(status: StatusAlerta) {
    try {
      await alterarStatus.mutateAsync({ id: alertaId, data: { status } });
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: getAlertasAdminControllerObterQueryKey(alertaId) }),
        queryClient.invalidateQueries({ queryKey: getAlertasAdminControllerListarQueryKey() }),
        queryClient.invalidateQueries({ queryKey: getAlertasAdminControllerResumoQueryKey() }),
      ]);
      toast.success(`Alerta marcado como ${ROTULO_STATUS[status].toLowerCase()}.`);
    } catch (e) {
      toast.error(e instanceof ApiError ? e.message : 'Não foi possível mudar o status do alerta.');
    }
  }

  const dados = alerta.data;
  const podeEditar = pode('admin_alertas.editar');

  return (
    <Dialog open onOpenChange={(v) => !v && aoFechar()}>
      <DialogContent className="sm:max-w-3xl max-h-[90vh] overflow-y-auto">
        {alerta.isPending ? (
          <div className="space-y-3">
            <Skeleton className="h-6 w-2/3" />
            <Skeleton className="h-24" />
          </div>
        ) : alerta.isError || !dados ? (
          <EstadoErro aoTentarDeNovo={() => void alerta.refetch()} />
        ) : (
          <>
            <DialogHeader>
              <div className="flex flex-wrap items-center gap-2">
                <Badge variant="outline" className={CLASSE_SEVERIDADE[dados.severidade]}>
                  {ROTULO_SEVERIDADE[dados.severidade]}
                </Badge>
                <Badge variant="outline">{ROTULO_STATUS[dados.status]}</Badge>
                <Badge variant="outline">{ROTULO_ORIGEM[dados.origem]}</Badge>
                <span className="font-mono text-xs text-muted-foreground">{dados.codigo}</span>
              </div>
              <DialogTitle className="pt-2">{dados.titulo}</DialogTitle>
              <DialogDescription className="whitespace-pre-wrap">{dados.mensagem}</DialogDescription>
            </DialogHeader>

            <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2 text-sm">
              <Item rotulo="Rota" valor={dados.rota ? `${dados.metodo ?? ''} ${dados.rota}`.trim() : '—'} mono />
              <Item rotulo="Status HTTP" valor={dados.statusHttp?.toString() ?? '—'} />
              <Item rotulo="Ocorrências" valor={dados.ocorrencias.toLocaleString('pt-BR')} />
              <Item rotulo="Primeira vez" valor={dataHora(dados.primeiraEm)} />
              <Item rotulo="Última vez" valor={dataHora(dados.ultimaEm)} />
              <Item rotulo="Último usuário" valor={dados.ultimoUsuarioNome ?? '—'} />
              <Item rotulo="Último ambiente" valor={dados.ultimoTenantNome ?? '—'} />
              <Item rotulo="Último request id" valor={dados.ultimoRequestId ?? '—'} mono />
              <Item rotulo="E-mail enviado" valor={dados.emailEnviadoEm ? dataHora(dados.emailEnviadoEm) : 'Não'} />
              {dados.resolvidoEm && (
                <Item
                  rotulo="Resolvido"
                  valor={`${dataHora(dados.resolvidoEm)}${dados.resolvidoPorNome ? ` por ${dados.resolvidoPorNome}` : ''}`}
                />
              )}
            </dl>

            {dados.detalhes && (
              <BlocoJson titulo="Detalhes" valor={dados.detalhes} />
            )}

            <section className="space-y-2">
              <h4 className="text-sm font-semibold text-foreground">Últimas ocorrências</h4>
              <p className="text-xs text-muted-foreground">
                Guardamos as 20 mais recentes. Senha, token, CPF e certificado aparecem como ***; arquivos,
                só nome, tipo e tamanho.
              </p>
              {ocorrencias.isPending ? (
                <Skeleton className="h-16" />
              ) : ocorrencias.isError || !ocorrencias.data ? (
                <EstadoErro aoTentarDeNovo={() => void ocorrencias.refetch()} />
              ) : ocorrencias.data.itens.length === 0 ? (
                <p className="text-sm text-muted-foreground">Nenhuma ocorrência guardada.</p>
              ) : (
                <ul className="space-y-2" aria-label="Ocorrências">
                  {ocorrencias.data.itens.map((ocorrencia) => (
                    <Ocorrencia key={ocorrencia.id} ocorrencia={ocorrencia} />
                  ))}
                </ul>
              )}
            </section>

            {podeEditar && (
              <DialogFooter className="gap-2">
                {dados.status !== 'ABERTO' && (
                  <Button variant="outline" onClick={() => void mudarPara('ABERTO')} disabled={alterarStatus.isPending}>
                    <RotateCcw className="w-4 h-4 mr-2" />
                    Reabrir
                  </Button>
                )}
                {dados.status !== 'SILENCIADO' && (
                  <Button variant="outline" onClick={() => void mudarPara('SILENCIADO')} disabled={alterarStatus.isPending}>
                    <BellOff className="w-4 h-4 mr-2" />
                    Silenciar
                  </Button>
                )}
                {dados.status !== 'RESOLVIDO' && (
                  <Button onClick={() => void mudarPara('RESOLVIDO')} disabled={alterarStatus.isPending}>
                    {alterarStatus.isPending ? (
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <CheckCircle2 className="w-4 h-4 mr-2" />
                    )}
                    Resolver
                  </Button>
                )}
              </DialogFooter>
            )}
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

function Item({ rotulo, valor, mono }: { rotulo: string; valor: string; mono?: boolean }) {
  return (
    <div className="min-w-0">
      <dt className="text-xs text-muted-foreground">{rotulo}</dt>
      <dd className={mono ? 'font-mono text-xs break-all text-foreground' : 'text-foreground'}>{valor}</dd>
    </div>
  );
}

function Ocorrencia({ ocorrencia }: { ocorrencia: OcorrenciaDeAlertaResponse }) {
  const [aberta, setAberta] = useState(false);
  const quem = [ocorrencia.usuarioNome, ocorrencia.tenantNome].filter(Boolean).join(' · ');

  return (
    <li className="rounded-md border border-border">
      <button
        type="button"
        className="w-full flex flex-wrap items-center justify-between gap-2 px-3 py-2 text-left text-sm hover:bg-muted/40"
        onClick={() => setAberta((v) => !v)}
        aria-expanded={aberta}
      >
        <span className="text-foreground">{dataHora(ocorrencia.criadoEm)}</span>
        <span className="text-xs text-muted-foreground">{quem || 'Sem usuário identificado'}</span>
      </button>
      {aberta && (
        <div className="space-y-3 border-t border-border p-3">
          {ocorrencia.requestId && (
            <p className="font-mono text-xs text-muted-foreground">request id: {ocorrencia.requestId}</p>
          )}
          <BlocoJson titulo="Requisição enviada" valor={ocorrencia.requisicao} />
          <BlocoJson titulo="Resposta de erro" valor={ocorrencia.resposta} />
        </div>
      )}
    </li>
  );
}

function BlocoJson({ titulo, valor }: { titulo: string; valor: unknown }) {
  const texto = jsonLegivel(valor);

  async function copiar() {
    try {
      await navigator.clipboard.writeText(texto);
      toast.success(`${titulo} copiada.`);
    } catch {
      toast.error('Não foi possível copiar.');
    }
  }

  return (
    <div className="space-y-1">
      <div className="flex items-center justify-between">
        <span className="text-xs font-medium text-foreground">{titulo}</span>
        <Button variant="ghost" size="sm" onClick={() => void copiar()} aria-label={`Copiar ${titulo.toLowerCase()}`}>
          <Copy className="w-3.5 h-3.5" />
        </Button>
      </div>
      <pre className="max-h-72 overflow-auto rounded-md bg-muted/50 p-3 text-xs leading-relaxed text-foreground whitespace-pre-wrap break-all">
        {texto}
      </pre>
    </div>
  );
}
